import React, { useCallback, useEffect, useMemo, useState } from "react";
import TreeItem from "@material-ui/lab/TreeItem";
import TreeView from "@material-ui/lab/TreeView";
import { groupBy } from "lodash";
import type { FC, ChangeEvent } from "react";
import AddBoxOutlinedIcon from "../../../../../icons/OpenTree";
import RemoveOutlinedIcon from "../../../../../icons/CloseTree";
import { Hierarchy } from "../../../../common";
import { useStyles, useParentStyles, useItemStyles, useDisabledItemStyles } from "./DataSourceTree.styles";
import type { ITreeProps } from "./DataSourceTree.types";
import { removeCapitalization } from "../../../../../utils";
import { StarBorderRounded, StarRounded } from "@material-ui/icons";
import { ISaveExportSettingsParams } from "../../SaveAsBulkExportStateButton/SaveAsBulkExportStateButton.state";
import { FavouriteItemType } from "../../../../favourites/types";
import { Button } from "@material-ui/core";
import { IBulkExportContextState, useBulkExportContext } from "../../../providers";
import { useBulkExportFavouriteQueryParams } from "../../../shared/hooks/useBulkExportFavouriteQueryParams";
import { useHistory } from "react-router";

export const DataSourceTree: FC<ITreeProps> = (props) => {
  const { value, onChange, items, expandedAll, search, addFavourite, removeFavourite, favouriteBulkItemId } = props;
  const history = useHistory();
  const { favoriteBulkId } = useBulkExportFavouriteQueryParams();
  const context = useBulkExportContext();
  const state = context?.state as IBulkExportContextState;

  const [expanded, setExpanded] = useState<string[]>(() => {
    const selectedItem = items.find((it) => it.id === value);
    const selectedItemParentId = selectedItem?.parentId;
    return selectedItemParentId !== undefined ? [String(selectedItemParentId)] : ([] as string[]);
  });

  const hierarchies = useMemo(() => {
    const allHierarchies = Hierarchy.from(
      items,
      (it) => String(it.id),
      (it) => String(it.id),
      (it) => it.name,
      (it) => String(it.parentId)
    );
    allHierarchies.forEach((h) => h.orderBy((it) => it.origin.id));

    return !search ? allHierarchies : Hierarchy.search(allHierarchies, search);
  }, [items]);
  const grouppedHierarchies = groupBy(hierarchies, (hierarchy) => hierarchy.origin.groupId);

  const classes = useStyles();
  const classesParent = useParentStyles();
  const classesItem = useItemStyles();
  const classesDisableItem = useDisabledItemStyles();

  const handleSelect = useCallback(
    (e: ChangeEvent<{}>, nodeId: string) => {
      const targetHierarchy = Hierarchy.flat(hierarchies).find((h) => h.id === nodeId);
      if (targetHierarchy !== undefined && !targetHierarchy.children?.length) {
        onChange?.(targetHierarchy.origin.id);
        if (favoriteBulkId) {
          history.push("/bulk-export");
        }
      }
    },
    [onChange, hierarchies, favoriteBulkId]
  );

  const handleExpand = useCallback(
    (e: ChangeEvent<{}>, nodeIds: string[]) => {
      setExpanded(nodeIds);
    },
    [setExpanded]
  );

  const handlerFavorites = (params: ISaveExportSettingsParams, isFavourite: boolean) => {
    if (!addFavourite || !removeFavourite) {
      return;
    }
    if (!isFavourite) {
      addFavourite(params);
    } else {
      removeFavourite({ type: FavouriteItemType.BulkExport, id: params?.favouriteId });
    }
  };

  const grouppedHierarchiesResult = Object.entries(grouppedHierarchies);

  useEffect(() => {
    if (favouriteBulkItemId) {
      const findIdInObjects = (results) => {
        let currentIds = [];
        results.forEach(([groupKey, groupHierarchies]) => {
          groupHierarchies.forEach((group) => {
            group.flatChildren.forEach((child) => {
              if (child?.origin?.id === favouriteBulkItemId) {
                const parentId = child?.origin?.id?.toString();
                const dataSetParentId = child?.parent.id;
                currentIds = [dataSetParentId as never, parentId as never];
              }
            });
          });
        });

        return currentIds;
      };
      const foundIds = findIdInObjects(grouppedHierarchiesResult);
      setExpanded(foundIds);
    }
  }, [favouriteBulkItemId]);
  
  grouppedHierarchiesResult.forEach(([groupKey, groupHierarchies]) => {
    groupHierarchies.forEach((hierarchy) => {
      hierarchy.children.forEach((h) => {
        const isFavourite = Boolean(h.origin?.isFavourite);
        if ((state.settingsModifiedForFavourites && state.dataSourceId === h.origin.id) || isFavourite) {
          h.origin.favoriteDisabled = false;
        } else {
          h.origin.favoriteDisabled = true;
        }
      });
    });
  });
  return (
    <TreeView
      className={classes.treeView}
      defaultCollapseIcon={<RemoveOutlinedIcon />}
      defaultExpandIcon={<AddBoxOutlinedIcon />}
      expanded={expandedAll ? hierarchies.map((x) => x.id) : expanded}
      selected={String(value)}
      onNodeSelect={handleSelect}
      onNodeToggle={handleExpand}
    >
      {!hierarchies?.length ? "No results" : null}
      {grouppedHierarchiesResult.map(([groupKey, groupHierarchies]) => (
        <div key={groupKey} className={classes.treeGroup}>
          {groupHierarchies.map((hierarchy) => (
            <TreeItem
              key={hierarchy.id}
              nodeId={hierarchy.id}
              label={removeCapitalization(hierarchy.name, ["APE"])}
              classes={classesParent}
            >
              {hierarchy.children.map(({ parentId, name, origin, id }) => {
                const isFavourite = Boolean(origin?.isFavourite);
                const currentFavorite = isFavourite ? <StarRounded /> : <StarBorderRounded />;
                const fullName = `${hierarchy.name}: ${name}`;
                return (
                  <div className={classesItem.item}>
                    <TreeItem
                      key={name}
                      nodeId={id}
                      label={removeCapitalization(name)}
                      classes={origin.disabled ? classesDisableItem : classesItem}
                    />
                    <Button
                      variant="outlined"
                      className={classesItem.iconFavourite}
                      disabled={origin.favoriteDisabled}
                      onClick={() =>
                        handlerFavorites(
                          {
                            id: +id,
                            favouriteId: origin?.favoriteId!,
                            parentId: +parentId!,
                            name: fullName,
                            type: FavouriteItemType.BulkExport,
                          },
                          isFavourite
                        )
                      }
                    >
                      {currentFavorite}
                    </Button>
                  </div>
                );
              })}
            </TreeItem>
          ))}
        </div>
      ))}
    </TreeView>
  );
};
